import React, { useState, useRef, useEffect, RefObject } from 'react';
import { useMsal } from '@azure/msal-react';
import {
    User as UserIcon,
    LogOut,
    Building2,
    Briefcase,
    MapPin,
    Phone,
    RefreshCw,
    ChevronDown,
    Shield,
    LucideIcon
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { useUserProfile } from '../../hooks/useUserProfile';
import { useAuth } from '../../hooks/useAuth';
import { UserRole } from '../../types';

export function UserProfileMenu() {
    const { instance } = useMsal();
    const { profile, loading, initials, refreshProfile, isAuthenticated } = useUserProfile();
    const { user: authUser } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    // Close menu on outside click
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Handle sign in/out
    const handleAuthAction = () => {
        if (isAuthenticated) {
            instance.logoutRedirect();
        } else {
            instance.loginRedirect();
        }
    };

    // Role display name
    const getRoleDisplayName = (role?: UserRole | string) => { // Accept string as fallback from raw auth user data
        switch (role) {
            case 'admin': return 'Administrator';
            case 'pm': return 'Project Manager';
            case 'owner': return 'Owner';
            case 'executive': return 'Executive';
            case 'normal':
            default: return 'User';
        }
    };

    if (!isAuthenticated) {
        return (
            <button
                onClick={handleAuthAction}
                title="Sign In"
                className="w-8 h-8 flex items-center justify-center rounded-full bg-siemens-deep-blue text-white hover:ring-2 hover:ring-siemens-petrol/30 transition-all"
            >
                <UserIcon className="w-4 h-4" />
            </button>
        );
    }

    // Role-based ring color
    const getRoleRingColor = (role?: string) => {
        switch (role) {
            case 'admin': return 'ring-red-400';
            case 'pm': return 'ring-blue-400';
            case 'executive': return 'ring-purple-400';
            case 'owner': return 'ring-green-400';
            default: return 'ring-slate-300';
        }
    };

    return (
        <div ref={menuRef} className="relative">
            {/* Avatar Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={cn(
                    "flex items-center gap-2 rounded-full transition-all",
                    "hover:ring-2 hover:ring-offset-1",
                    isOpen ? `ring-2 ring-offset-1 ${getRoleRingColor(authUser?.role)}` : `hover:${getRoleRingColor(authUser?.role)}`
                )}
                title={getRoleDisplayName(authUser?.role)}
            >
                {/* Avatar */}
                <div className="relative">
                    {profile?.photoUrl ? (
                        <img
                            src={profile.photoUrl}
                            alt={profile.displayName || "User"}
                            className={cn(
                                "w-8 h-8 rounded-full object-cover ring-2",
                                getRoleRingColor(authUser?.role)
                            )}
                        />
                    ) : (
                        <div className={cn(
                            "w-8 h-8 flex items-center justify-center rounded-full ring-2",
                            "bg-siemens-petrol text-white text-xs font-bold",
                            getRoleRingColor(authUser?.role)
                        )}>
                            {loading ? (
                                <RefreshCw className="w-3 h-3 animate-spin" />
                            ) : (
                                initials
                            )}
                        </div>
                    )}
                    {/* Online indicator */}
                    <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-siemens-elevation-base-1" />
                </div>
                <ChevronDown className={cn(
                    "w-3 h-3 text-siemens-text-soft transition-transform",
                    isOpen && "rotate-180"
                )} />
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <div
                    className={cn(
                        "absolute right-0 top-full mt-2 z-50",
                        "w-80 bg-white dark:bg-slate-800",
                        "border border-slate-200 dark:border-slate-700",
                        "rounded-xl shadow-xl overflow-hidden"
                    )}
                    style={{
                        animation: 'fadeSlideIn 0.2s ease-out'
                    }}
                >
                    {/* Profile Header */}
                    <div className="p-4 bg-gradient-to-br from-siemens-petrol to-siemens-deep-blue">
                        <div className="flex items-start gap-3">
                            {/* Large Avatar */}
                            {profile?.photoUrl ? (
                                <img
                                    src={profile.photoUrl}
                                    alt={profile.displayName || "User"}
                                    className="w-16 h-16 rounded-full object-cover border-2 border-white/20"
                                />
                            ) : (
                                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-white/20 text-white text-xl font-bold border-2 border-white/20">
                                    {initials}
                                </div>
                            )}
                            <div className="flex-1 min-w-0">
                                <h3 className="text-lg font-semibold text-white truncate">
                                    {profile?.displayName || 'User'}
                                </h3>
                                <p className="text-sm text-white/80 truncate">
                                    {profile?.email}
                                </p>
                                {/* Role Badge */}
                                <div className="mt-2 flex items-center gap-1.5">
                                    <Shield className="w-3 h-3 text-white/70" />
                                    <span className={cn(
                                        "text-xs font-medium px-2 py-0.5 rounded-full",
                                        "bg-white/20 text-white"
                                    )}>
                                        {getRoleDisplayName(authUser?.role)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Profile Details */}
                    <div className="p-3 space-y-1 border-b border-slate-200 dark:border-slate-700">
                        {profile?.jobTitle && (
                            <ProfileDetailRow
                                icon={Briefcase}
                                label="Job Title"
                                value={profile.jobTitle}
                            />
                        )}
                        {profile?.department && (
                            <ProfileDetailRow
                                icon={Building2}
                                label="Department"
                                value={profile.department}
                            />
                        )}
                        {profile?.officeLocation && (
                            <ProfileDetailRow
                                icon={MapPin}
                                label="Office"
                                value={profile.officeLocation}
                            />
                        )}
                        {/* {profile?.mobilePhone && (
                            <ProfileDetailRow
                                icon={Phone}
                                label="Mobile"
                                value={profile.mobilePhone}
                            />
                        )} */}
                        {/* Commented out mobile phone as user doesn't contain it in types currently, though graphService fetches it */}

                        {!profile?.jobTitle && !profile?.department && !profile?.officeLocation && (
                            <p className="text-xs text-siemens-text-soft py-2 text-center">
                                Profile details not available in Azure AD
                            </p>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="p-2">
                        <button
                            onClick={refreshProfile}
                            className={cn(
                                "w-full flex items-center gap-3 px-3 py-2 rounded-lg",
                                "text-sm text-siemens-deep-blue dark:text-siemens-text-standard",
                                "hover:bg-slate-100 dark:hover:bg-slate-700",
                                "transition-colors"
                            )}
                        >
                            <RefreshCw className="w-4 h-4 text-siemens-text-soft" />
                            Refresh Profile
                        </button>
                        <button
                            onClick={handleAuthAction}
                            className={cn(
                                "w-full flex items-center gap-3 px-3 py-2 rounded-lg",
                                "text-sm text-red-600 dark:text-red-400",
                                "hover:bg-red-50 dark:hover:bg-red-500/10",
                                "transition-colors"
                            )}
                        >
                            <LogOut className="w-4 h-4" />
                            Sign Out
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// Helper component for profile detail rows
function ProfileDetailRow({ icon: Icon, label, value }: { icon: LucideIcon; label: string; value: string }) {
    return (
        <div className="flex items-center gap-3 px-2 py-1.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50">
            <Icon className="w-4 h-4 text-siemens-text-soft flex-shrink-0" />
            <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-wider text-siemens-text-soft">
                    {label}
                </p>
                <p className="text-sm text-siemens-deep-blue dark:text-siemens-text-standard truncate">
                    {value}
                </p>
            </div>
        </div>
    );
}

export default UserProfileMenu;
